// src/components/SanctuarySettings.jsx
import React, { useState, useEffect } from "react";
import { X, Search, Moon, Sun, Compass, Check, Globe, Trash2, AlertTriangle } from "lucide-react";
import api from "../api/axios";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";

export default function SanctuarySettings({ isOpen, onClose }) {
    const { language, switchLanguage, t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState("");
    const [isClosing, setIsClosing] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [sliderValue, setSliderValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    
    // Theme Logic (Extracted from ThemeToggle)
    const [isDark, setIsDark] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("theme") === "dark" ||
                (!("theme" in localStorage) && window.matchMedia("(prefers-color-scheme: dark)").matches);
        }
        return false;
    });

    useEffect(() => {
        const root = window.document.documentElement;
        if (isDark) {
            root.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            root.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    }, [isDark]);

    const languages = [
        { id: 'en', label: 'English', sub: t('set_lang_en_sub') },
        { id: 'hi', label: 'Hinglish', sub: t('set_lang_hi_sub') },
    ];

    const filteredLanguages = languages.filter(lang => 
        lang.label.toLowerCase().includes(searchQuery.toLowerCase()) || 
        lang.sub.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                {/* Backdrop */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/40 backdrop-blur-md"
                />

                {/* Modal */}
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative w-full max-w-md bg-white/90 dark:bg-gray-900/90 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 dark:border-gray-800"
                >
                    {/* Header */}
                    <div className="p-6 md:p-8 flex justify-between items-center border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                <Compass className="w-5 h-5 text-brand-blue" />
                            </div>
                            <h2 className="text-xl font-display font-medium text-gray-900 dark:text-gray-100">{t('set_title')}</h2>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                            <X className="w-5 h-5 text-gray-400" />
                        </button>
                    </div>

                    <div className="p-6 md:p-8 space-y-8 max-h-[70vh] overflow-y-auto scrollbar-hide">
                        
                        {/* 🌓 INTERNAL WEATHER (THEME) */}
                        <section className="space-y-4">
                            <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1">{t('set_theme_title')}</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <button 
                                    onClick={() => setIsDark(false)}
                                    className={`flex items-center justify-center gap-3 p-4 rounded-2xl border transition-all ${!isDark ? 'bg-amber-50 border-amber-200 text-amber-600 shadow-sm' : 'border-gray-100 dark:border-gray-800 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                >
                                    <Sun className="w-5 h-5" />
                                    <span className="font-medium">{t('set_light')}</span>
                                </button>
                                <button 
                                    onClick={() => setIsDark(true)}
                                    className={`flex items-center justify-center gap-3 p-4 rounded-2xl border transition-all ${isDark ? 'bg-indigo-900/20 border-indigo-500/30 text-indigo-400 shadow-sm' : 'border-gray-100 dark:border-gray-800 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                >
                                    <Moon className="w-5 h-5" />
                                    <span className="font-medium">{t('set_dark')}</span>
                                </button>
                            </div>
                        </section>

                        {/* 🗣️ SOUL'S LANGUAGE */}
                        <section className="space-y-4">
                            <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1">{t('set_lang_title')}</h3>
                            
                            {/* Search */}
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-brand-blue transition-colors" />
                                <input 
                                    type="text" 
                                    placeholder={t('set_lang_search')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 transition-all text-sm"
                                />
                            </div>

                            <div className="space-y-2 pt-2">
                                <p className="text-xs italic text-gray-500 dark:text-gray-400 px-1 mb-4">
                                    "{t('set_lang_resonate')}"
                                </p>
                                
                                <div className="space-y-2">
                                    {filteredLanguages.map((lang) => (
                                        <button
                                            key={lang.id}
                                            onClick={() => switchLanguage(lang.id)}
                                            className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all border ${language === lang.id ? 'bg-blue-50 dark:bg-brand-blue/10 border-brand-blue/20 text-brand-blue' : 'border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2 rounded-xl transition-colors ${language === lang.id ? 'bg-brand-blue/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                                    <Globe className="w-4 h-4" />
                                                </div>
                                                <div className="text-left">
                                                    <div className="font-medium">{lang.label}</div>
                                                    <div className="text-[10px] opacity-60">{lang.sub}</div>
                                                </div>
                                            </div>
                                            {language === lang.id && (
                                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                                                    <Check className="w-5 h-5" />
                                                </motion.div>
                                            )}
                                        </button>
                                    ))}
                                    {filteredLanguages.length === 0 && (
                                        <div className="text-center py-8 text-gray-400 text-sm italic">
                                            {t('set_lang_missing')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* ⚠️ DANGER ZONE */}
                        <section className="pt-8 border-t border-gray-100 dark:border-gray-800 space-y-4">
                            {!confirmClose ? (
                                <button 
                                    onClick={() => setConfirmClose(true)}
                                    className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all text-xs font-bold uppercase tracking-widest"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {t('set_close_account')}
                                </button>
                            ) : (
                                <div className="space-y-4 p-4 rounded-2xl bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-bold text-red-600 dark:text-red-400">{t('set_close_warning_title')}</h4>
                                            <p className="text-[11px] text-red-500/70 mt-1 leading-relaxed">
                                                {t('set_close_warning_desc')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-4 pt-2">
                                        <div className="relative h-14 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden flex items-center px-2">
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                    {sliderValue > 90 ? "Release to Delete" : t('set_slide_to_confirm')}
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={sliderValue}
                                                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                                                onMouseUp={() => {
                                                    if (sliderValue < 95) setSliderValue(0);
                                                    else handleAccountClosure();
                                                }}
                                                onTouchEnd={() => {
                                                    if (sliderValue < 95) setSliderValue(0);
                                                    else handleAccountClosure();
                                                }}
                                                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                            />
                                            <motion.div 
                                                className="h-10 w-10 bg-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20"
                                                style={{ x: `${sliderValue * 2.8}px` }}
                                            >
                                                <Trash2 className="w-5 h-5 text-white" />
                                            </motion.div>
                                        </div>
                                        
                                        <button 
                                            onClick={() => {
                                                setConfirmClose(false);
                                                setSliderValue(0);
                                            }}
                                            className="w-full py-3 rounded-xl bg-white dark:bg-gray-800 text-gray-500 text-xs font-bold uppercase tracking-widest border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                                        >
                                            {t('set_cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>

                        <div className="pt-4 text-center">
                            <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                                {t('set_tagline')}
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );

    async function handleAccountClosure() {
        try {
            setIsLoading(true);
            await api.delete("/auth/close-account");
            window.location.href = "/login?closed=true";
        } catch (err) {
            console.error("Closure failed:", err);
            setConfirmClose(false);
            setSliderValue(0);
        } finally {
            setIsLoading(false);
        }
    }
}
